import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Eye, Calendar, User, FileText, Trash2, Archive, FolderOpen, Download, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAbortController } from '@/hooks/useSafeEffect';
const Files = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fileStatusTab, setFileStatusTab] = useState('actif'); // actif or clos
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [actifCount, setActifCount] = useState(0);
  const [closCount, setClosCount] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [newFile, setNewFile] = useState({
    client_id: user?.role === 'client' ? user.id : '',
    client_name: user?.role === 'client' ? user.full_name : '',
    assigned_employees: [],
    metadata: {
      file_type: 'immigration'
    }
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // ✅ Hook pour gérer l'annulation des requêtes
  const { signal, isMounted } = useAbortController();

  useEffect(() => {
    const abortController = new AbortController();
    let mounted = true;

    const loadData = async () => {
      try {
        await fetchFiles(abortController.signal, { current: mounted });
        if (user?.role === 'admin' || user?.role === 'employee') {
          await Promise.all([
            fetchEmployees(abortController.signal, { current: mounted }),
            fetchClients(abortController.signal, { current: mounted })
          ]);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error loading data:', error);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    filterFiles();
  }, [files, searchTerm, statusFilter, fileStatusTab]);

  const fetchFiles = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setFiles(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        toast.error(t('error'));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchEmployees = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setEmployees(response.data.filter(u => u.role === 'employee'));
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch employees:', error);
      }
    }
  };

  const fetchClients = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/users?role_filter=client`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setClients(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch clients:', error);
      }
    }
  };

  const handleDeleteFile = async (fileId, e) => {
    e.stopPropagation(); // Prevent card click navigation
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce dossier ? Cette action est irréversible.')) {
      return;
    }

    try {
      await axios.delete(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dossier supprimé avec succès');
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleChangeFileStatus = async (fileId, newStatus, e) => {
    e.stopPropagation(); // Prevent card click navigation
    
    const action = newStatus === 'clos' ? 'clore' : 'réouvrir';
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} ce dossier ?`)) {
      return;
    }

    try {
      await axios.put(`${API}/files/${fileId}/status?file_status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Dossier ${newStatus === 'clos' ? 'clos' : 'réouvert'} avec succès`);
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  const handleDownloadReport = async (fileId, fileNumber, e) => {
    e.stopPropagation(); // Prevent card click navigation
    
    try {
      toast.info('Génération du rapport en cours...');
      
      const response = await axios.get(`${API}/files/${fileId}/generate-report-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob' // Important pour recevoir un fichier binaire
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rapport_${fileNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Rapport téléchargé avec succès');
    } catch (error) {
      console.error('Erreur lors du téléchargement du rapport:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération du rapport');
    }
  };

  const handleGenerateFinalInvoice = async (fileId, fileNumber, e) => {
    e.stopPropagation(); // Prevent card click navigation
    
    if (!window.confirm(`Générer la facture finale pour le dossier ${fileNumber}? Cette action créera une facture incluant toutes les tâches terminées.`)) {
      return;
    }
    
    try {
      toast.info('Génération de la facture finale en cours...');
      
      await axios.post(
        `${API}/files/${fileId}/generate-final-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Facture finale générée avec succès! Consultez la page Factures.');
    } catch (error) {
      console.error('Erreur lors de la génération de la facture:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération de la facture');
    }
  };

  const filterFiles = () => {
    let filtered = files;

    // Count actif and clos files
    const actifFiles = files.filter(f => (f.file_status || 'actif') === 'actif');
    const closFiles = files.filter(f => (f.file_status || 'actif') === 'clos');
    setActifCount(actifFiles.length);
    setClosCount(closFiles.length);

    // Filter by file_status (actif/clos)
    filtered = filtered.filter((file) => {
      const status = file.file_status || 'actif';
      return status === fileStatusTab;
    });

    if (searchTerm) {
      filtered = filtered.filter(
        (file) =>
          file.file_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
          file.client_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((file) => file.status === statusFilter);
    }

    setFilteredFiles(filtered);
  };

  const handleCreateFile = async () => {
    // Validation
    if (!newFile.client_id || !newFile.client_name) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    try {
      await axios.post(`${API}/files`, newFile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dossier créé avec succès');
      setCreateDialogOpen(false);
      fetchFiles();
      setNewFile({
        client_id: user?.role === 'client' ? user.id : '',
        client_name: user?.role === 'client' ? user.full_name : '',
        assigned_employees: [],
        metadata: {
          file_type: 'immigration'
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du dossier');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ouvert: 'bg-blue-100 text-blue-800 border-blue-200',
      en_cours: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      soumission_ircc: 'bg-purple-100 text-purple-800 border-purple-200',
      en_traitement: 'bg-orange-100 text-orange-800 border-orange-200',
      decision: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      ferme: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="files-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {t('files')}
            </h1>
            <p className="text-lg text-gray-600">
              {filteredFiles.length} {filteredFiles.length === 1 ? 'dossier' : 'dossiers'}
            </p>
          </div>

          {(user?.role === 'admin' || user?.role === 'employee') && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700 shadow-lg"
                  data-testid="create-file-btn"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  {t('create_file')}
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="create-file-dialog">
                <DialogHeader>
                  <DialogTitle>{t('create_file')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {user?.role !== 'client' && (
                    <div className="space-y-2">
                      <Label>Sélectionner un Client *</Label>
                      <Select
                        value={newFile.client_id}
                        onValueChange={(value) => {
                          const selectedClient = clients.find(c => c.id === value);
                          setNewFile({ 
                            ...newFile, 
                            client_id: value,
                            client_name: selectedClient ? selectedClient.full_name : ''
                          });
                        }}
                      >
                        <SelectTrigger data-testid="client-select">
                          <SelectValue placeholder="Choisir un client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map(client => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.full_name} ({client.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {clients.length === 0 && (
                        <p className="text-sm text-red-600">
                          Aucun client disponible. Veuillez d'abord créer un compte client dans "Clients".
                        </p>
                      )}
                    </div>
                  )}
                  {/* Type de Dossier */}
                  <div className="space-y-2">
                    <Label>Type de Dossier *</Label>
                    <Select
                      value={newFile.metadata?.file_type || 'immigration'}
                      onValueChange={(value) => setNewFile({ 
                        ...newFile, 
                        metadata: { ...newFile.metadata, file_type: value }
                      })}
                    >
                      <SelectTrigger data-testid="file-type-select">
                        <SelectValue placeholder="Choisir un type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immigration">Immigration</SelectItem>
                        <SelectItem value="consultation">Consultation</SelectItem>
                        <SelectItem value="visa">Visa</SelectItem>
                        <SelectItem value="permis_travail">Permis de Travail</SelectItem>
                        <SelectItem value="citoyennete">Citoyenneté</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {user?.role === 'admin' && employees.length > 0 && (
                    <div className="space-y-2">
                      <Label>{t('assigned_to')}</Label>
                      <Select
                        onValueChange={(value) => setNewFile({ ...newFile, assigned_employees: [value] })}
                      >
                        <SelectTrigger data-testid="assign-employee-select">
                          <SelectValue placeholder="Sélectionner un employé" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map((emp) => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button
                    onClick={handleCreateFile}
                    className="w-full bg-gradient-to-r from-red-600 to-blue-600"
                    data-testid="submit-create-file-btn"
                  >
                    {t('create_file')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* File Status Tabs */}
        <div className="flex space-x-2 bg-gray-100 p-1.5 rounded-lg w-fit shadow-inner">
          <button
            onClick={() => setFileStatusTab('actif')}
            className={`px-6 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
              fileStatusTab === 'actif'
                ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-lg transform scale-105'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            data-testid="actif-tab"
          >
            <span>📂 Dossiers Actifs</span>
            <Badge 
              className={`${
                fileStatusTab === 'actif'
                  ? 'bg-white/20 text-white border-white/30'
                  : 'bg-gray-200 text-gray-700 border-gray-300'
              }`}
            >
              {actifCount}
            </Badge>
          </button>
          <button
            onClick={() => setFileStatusTab('clos')}
            className={`px-6 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
              fileStatusTab === 'clos'
                ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-lg transform scale-105'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            data-testid="clos-tab"
          >
            <span>🗄️ Dossiers Clos</span>
            <Badge 
              className={`${
                fileStatusTab === 'clos'
                  ? 'bg-white/20 text-white border-white/30'
                  : 'bg-gray-200 text-gray-700 border-gray-300'
              }`}
            >
              {closCount}
            </Badge>
          </button>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder={t('search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="search-files-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-12" data-testid="status-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="ouvert">{t('ouvert')}</SelectItem>
                  <SelectItem value="en_cours">{t('en_cours')}</SelectItem>
                  <SelectItem value="soumission_ircc">{t('soumission_ircc')}</SelectItem>
                  <SelectItem value="en_traitement">{t('en_traitement')}</SelectItem>
                  <SelectItem value="decision">{t('decision')}</SelectItem>
                  <SelectItem value="ferme">{t('ferme')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Files Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredFiles.map((file) => (
            <Card
              key={file.id}
              className="border-0 shadow-xl hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
              onClick={() => navigate(`/files/${file.id}`)}
              data-testid={`file-card-${file.id}`}
            >
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-xl">
                    <FileText className="inline mr-2 h-5 w-5" />
                    {file.file_number}
                  </CardTitle>
                  <Badge className={`${getStatusColor(file.status)} border`}>
                    {t(file.status)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-600">
                    <User className="h-4 w-4 mr-2" />
                    <span className="text-sm font-medium">{file.client_name}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span className="text-sm">
                      {format(new Date(file.created_at), 'dd/MM/yyyy')}
                    </span>
                  </div>
                  {file.metadata?.file_type && (
                    <div className="flex items-center">
                      <Badge variant="outline" className="text-xs">
                        {file.metadata.file_type === 'immigration' && '🛂 Immigration'}
                        {file.metadata.file_type === 'consultation' && '💼 Consultation'}
                        {file.metadata.file_type === 'visa' && '🛃 Visa'}
                        {file.metadata.file_type === 'permis_travail' && '🏢 Permis de Travail'}
                        {file.metadata.file_type === 'citoyennete' && '🇨🇦 Citoyenneté'}
                        {file.metadata.file_type === 'autre' && '📋 Autre'}
                        {!['immigration', 'consultation', 'visa', 'permis_travail', 'citoyennete', 'autre'].includes(file.metadata.file_type) && file.metadata.file_type}
                      </Badge>
                    </div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/files/${file.id}`);
                      }}
                      data-testid={`view-file-btn-${file.id}`}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      {t('view')}
                    </Button>
                    {/* Bouton de téléchargement du rapport pour les dossiers clos (admin et employés) */}
                    {(user?.role === 'admin' || user?.role === 'employee') && (file.file_status || 'actif') === 'clos' && (
                      <>
                        <Button
                          variant="outline"
                          className="border-blue-300 text-blue-600 hover:bg-blue-50"
                          onClick={(e) => handleDownloadReport(file.id, file.file_number, e)}
                          data-testid={`download-report-btn-${file.id}`}
                          title="Télécharger le rapport PDF complet"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {/* Bouton de génération de facture finale (admin uniquement) */}
                        {user?.role === 'admin' && (
                          <Button
                            variant="outline"
                            className="border-purple-300 text-purple-600 hover:bg-purple-50"
                            onClick={(e) => handleGenerateFinalInvoice(file.id, file.file_number, e)}
                            data-testid={`generate-invoice-btn-${file.id}`}
                            title="Générer la facture finale avec tâches terminées"
                          >
                            <Receipt className="h-4 w-4" />
                          </Button>
                        )}
                      </>
                    )}
                    {user?.role === 'admin' && (
                      <>
                        <Button
                          variant="outline"
                          className={`${
                            (file.file_status || 'actif') === 'actif'
                              ? 'border-orange-300 text-orange-600 hover:bg-orange-50'
                              : 'border-green-300 text-green-600 hover:bg-green-50'
                          }`}
                          onClick={(e) => handleChangeFileStatus(
                            file.id,
                            (file.file_status || 'actif') === 'actif' ? 'clos' : 'actif',
                            e
                          )}
                          data-testid={`status-file-btn-${file.id}`}
                          title={(file.file_status || 'actif') === 'actif' ? 'Clore le dossier' : 'Réouvrir le dossier'}
                        >
                          {(file.file_status || 'actif') === 'actif' ? (
                            <Archive className="h-4 w-4" />
                          ) : (
                            <FolderOpen className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={(e) => handleDeleteFile(file.id, e)}
                          data-testid={`delete-file-btn-${file.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredFiles.length === 0 && (
          <Card className="border-0 shadow-xl">
            <CardContent className="p-12 text-center text-gray-500">
              <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-semibold mb-2">
                {fileStatusTab === 'actif' ? '📂 Aucun dossier actif' : '🗄️ Aucun dossier clos'}
              </p>
              <p className="text-sm text-gray-400">
                {fileStatusTab === 'actif' 
                  ? 'Créez un nouveau dossier pour commencer' 
                  : 'Les dossiers clos apparaîtront ici'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Files;